import styled from 'styled-components';
import theme from '../theme';

export const PageSubtitle = styled.p`
  margin: -${theme.spacing.md} 0 ${theme.spacing.lg};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.small.size};
  line-height: 1.5;
  max-width: 720px;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
`;

export const DocRow = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
`;

export const DocMain = styled.div`
  flex: 1 1 360px;
  min-width: 0;
`;

export const DocTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: 6px;
`;

export const DocSummary = styled.p`
  margin: 6px 0 10px;
  color: ${theme.colors.text};
  font-size: ${theme.typography.body.size};
  line-height: 1.5;
`;

export const DocMeta = styled.div`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.tiny.size};
  line-height: 1.6;
`;

export const DocActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  /* Shrinkable and capped at the card width, or the row sizes to its buttons
     and never wraps: on phones Remove sat past the card's right edge. */
  flex: 0 1 auto;
  max-width: 100%;
`;

const STATUS_COLORS = {
  ready: { fg: '#047857', bg: 'rgba(16, 185, 129, 0.12)' },
  queued: { fg: '#b45309', bg: 'rgba(245, 158, 11, 0.14)' },
  processing: { fg: '#b45309', bg: 'rgba(245, 158, 11, 0.14)' },
  uploading: { fg: '#b45309', bg: 'rgba(245, 158, 11, 0.14)' },
  error: { fg: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  font-size: ${theme.typography.tiny.size};
  font-weight: 600;
  padding: 3px 10px;
  border-radius: ${theme.radii.full};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ $status }) => (STATUS_COLORS[$status] || STATUS_COLORS.processing).fg};
  background: ${({ $status }) => (STATUS_COLORS[$status] || STATUS_COLORS.processing).bg};
`;

export const PrimaryButton = styled.button`
  font-family: ${theme.typography.fontFamily};
  background: ${theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${theme.radii.sm};
  padding: 9px 18px;
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const GhostButton = styled.button`
  font-family: ${theme.typography.fontFamily};
  background: transparent;
  color: ${theme.colors.primary};
  border: 1.5px solid ${theme.colors.primary};
  border-radius: ${theme.radii.sm};
  padding: 8px 16px;
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${theme.colors.primaryGhost}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const DangerButton = styled(GhostButton)`
  color: #b91c1c;
  border-color: #b91c1c;
  &:hover { background: rgba(220, 38, 38, 0.08); }
`;

export const ErrorText = styled.div`
  margin-top: 6px;
  color: #b91c1c;
  font-size: ${theme.typography.small.size};
`;

export const Notice = styled.div`
  background: ${theme.colors.primaryGhost};
  color: ${theme.colors.text};
  border-radius: ${theme.radii.sm};
  padding: 12px 16px;
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.small.size};
  line-height: 1.5;
`;

export const ErrorBanner = styled.div`
  background: rgba(220, 38, 38, 0.08);
  color: #b91c1c;
  border-radius: ${theme.radii.sm};
  padding: 12px 16px;
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.small.size};
`;

export const QuestionCountSelect = styled.select`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  padding: 7px 10px;
  border: 1.5px solid ${theme.colors.border || '#d0d7de'};
  border-radius: ${theme.radii.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
`;

/* --- Folders, grouping and bulk actions ---------------------------------- */

// Rail beside the list on desktop; a row of chips above it on phones.
export const Layout = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: ${theme.spacing.lg};
  align-items: start;
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${theme.spacing.md};
  }
`;

export const Rail = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: sticky;
  top: 12px;
  min-width: 0;
  @media (max-width: 760px) {
    position: static;
    flex-direction: row;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 6px;
  }
`;

export const RailItem = styled.button`
  font-family: ${theme.typography.fontFamily};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  text-align: left;
  padding: 8px 12px;
  border-radius: ${theme.radii.sm};
  border: 1.5px solid transparent;
  background: ${({ $active }) => ($active ? theme.colors.primaryGhost : 'transparent')};
  color: ${({ $active }) => ($active ? theme.colors.primary : theme.colors.text)};
  font-size: ${theme.typography.small.size};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  &:hover { background: ${theme.colors.primaryGhost}; }
  & > span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  @media (max-width: 760px) {
    width: auto;
    white-space: nowrap;
    border-color: ${({ $active }) => (theme.colors.border || '#d0d7de')};
    border-color: ${({ $active }) => ($active ? theme.colors.primary : (theme.colors.border || '#d0d7de'))};
  }
`;

export const RailCount = styled.span`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.tiny.size};
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
`;

export const RailActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 2px 12px 8px;
  @media (max-width: 760px) { padding: 0; }
`;

export const Content = styled.div`
  min-width: 0;
`;

export const Toolbar = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

export const SearchInput = styled.input`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  padding: 8px 12px;
  border: 1.5px solid ${theme.colors.border || '#d0d7de'};
  border-radius: ${theme.radii.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  flex: 1 1 220px;
  min-width: 0;
`;

export const StatusLine = styled.div`
  background: ${theme.colors.primaryGhost};
  color: ${theme.colors.text};
  border-radius: ${theme.radii.sm};
  padding: 10px 14px;
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.small.size};
  line-height: 1.5;
`;

export const GroupHeading = styled.h3`
  margin: ${theme.spacing.lg} 0 ${theme.spacing.sm};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textMuted};
  &:first-of-type { margin-top: 0; }
`;

export const GroupToggle = styled.button`
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const SelectBar = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  background: ${theme.colors.surface};
  border: 1.5px solid ${theme.colors.primary};
  border-radius: ${theme.radii.sm};
  padding: 10px 14px;
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.text};
`;

export const SelectCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${theme.colors.primary};
  cursor: pointer;
  flex: 0 0 auto;
`;

export const MoveSelect = styled(QuestionCountSelect)`
  max-width: 220px;
`;
